// The owner's Versions card on a voice page (lib/voiceVersions.ts): every
// version of this voice, which one is live, and "Train a new version".
// Training a new version is Studio; switching the live one is open to every
// owner, so a downgrade doesn't strand anyone on a version they don't want.
import React, { useState } from "react";
import Link from "next/link";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { toast } from "react-hot-toast";
import { Plus } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";
import { getIdToken } from "@/utils/firebaseAuth";
import { useAuth } from "@/context/AuthProvider";
import { useModal } from "@/context/ModalContext";
import useSubscription from "@/hooks/useSubscription";
import UpgradeModal from "@/components/modals/UpgradeModal";
import CreatorUpsellSheet, {
  canUpsellToPower,
} from "@/components/upsell/CreatorUpsellSheet";
import type { VoiceVersion } from "@/lib/voiceVersions";

dayjs.extend(relativeTime);

export default function VoiceVersions({
  urlParam,
  versions,
}: {
  urlParam: string;
  versions: VoiceVersion[];
}) {
  const t = useT();
  const { user } = useAuth();
  const { openModal } = useModal();
  const { hasPowerPlan } = useSubscription();
  const [rows, setRows] = useState(versions);
  const [busy, setBusy] = useState<string | null>(null);
  const [upsell, setUpsell] = useState(false);
  const trainHref = `/train?versionOf=${encodeURIComponent(urlParam)}`;

  const makeLive = async (v: VoiceVersion) => {
    setBusy(v._id);
    try {
      const token = await getIdToken();
      await axios.post(
        "/api/model/setLiveVersion",
        { modelId: v._id },
        { headers: { Authorization: token } },
      );
      setRows((all) => all.map((r) => ({ ...r, live: r._id === v._id })));
      toast.success(t("voice.versions.madeLive", { n: v.versionNumber }));
    } catch {
      toast.error(t("voice.versions.error"));
    } finally {
      setBusy(null);
    }
  };

  const onNew = (e: React.MouseEvent) => {
    if (hasPowerPlan()) return;
    e.preventDefault();
    if (canUpsellToPower(user)) return setUpsell(true);
    openModal(
      "upgrade-modal",
      <UpgradeModal from="voice-versions" dailyLimit={false} plan="power" />,
      { className: "tw-p-0" },
    );
  };

  return (
    <section className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-4 md:tw-rounded-[32px] md:tw-p-5">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-min-w-0 tw-flex-col">
          <h2 className="jam-display tw-m-0 tw-text-[20px] tw-font-extrabold tw-text-jam-ink">
            {t("voice.versions.title")}
          </h2>
          <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-jam-muted">
            {t("voice.versions.hint")}
          </p>
        </div>
        <Link
          href={trainHref}
          onClick={onNew}
          className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-ink tw-px-4 tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-white tw-no-underline hover:tw-bg-jam-deep"
        >
          <Plus size={16} strokeWidth={3} />
          {t("voice.versions.new")}
        </Link>
      </div>

      {rows.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
          {rows.map((v) => (
            <li
              key={v._id}
              className="tw-flex tw-min-h-[56px] tw-items-center tw-justify-between tw-gap-3 tw-rounded-[18px] tw-bg-jam-lav tw-px-4 tw-py-2"
            >
              <Link
                href={`/${v.urlParam}`}
                className="tw-flex tw-min-w-0 tw-items-baseline tw-gap-2 tw-text-jam-ink tw-no-underline"
              >
                <span className="tw-text-[15px] tw-font-extrabold">
                  {t("voice.versions.item", { n: v.versionNumber })}
                </span>
                {v.createdAt && (
                  <span className="tw-text-[13px] tw-font-semibold tw-text-jam-muted">
                    {dayjs(v.createdAt).fromNow()}
                  </span>
                )}
              </Link>
              {v.training ? (
                <span className="tw-text-[13px] tw-font-extrabold tw-text-jam-muted">
                  {t("voice.versions.training")}
                </span>
              ) : v.failed ? (
                <span className="tw-text-[13px] tw-font-extrabold tw-text-[#C2185B]">
                  {t("voice.versions.failed")}
                </span>
              ) : v.live ? (
                <span className="tw-rounded-full tw-bg-jam-lime tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-jam-ink">
                  {t("voice.versions.live")}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => makeLive(v)}
                  className="tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-white tw-px-3.5 tw-py-1.5 tw-text-[13px] tw-font-extrabold tw-text-jam-ink disabled:tw-opacity-60"
                >
                  {t("voice.versions.makeLive")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <CreatorUpsellSheet
        plan="power"
        open={upsell}
        reason="versions"
        onClose={() => setUpsell(false)}
        onUpgraded={() => {
          setUpsell(false);
          window.location.href = trainHref;
        }}
      />
    </section>
  );
}
